/**
 * Google Analytics 4 — deliberately restricted to Cedar Vault's PUBLIC pages.
 *
 * Why this file exists instead of a plain gtag snippet in public/index.html:
 *
 *   1. Our privacy policy promises we do not build behavioral profiles of
 *      customers and do not use their information for advertising. A blanket tag
 *      would break that promise the moment someone signs in.
 *   2. GA4 "Enhanced measurement" tracks SPA route changes off browser history
 *      events by default. With a blanket tag, paths like /vault/47/print would be
 *      sent to Google — a vault identifier leaving our system for no good reason.
 *
 * So: the gtag script is never even injected unless the visitor is on a public,
 * pre-login page, and it is hard-disabled the instant they reach vault content.
 *
 * Marketing still gets the whole funnel it needs — site visit, register page
 * view, and a sign_up conversion — without a single authenticated page view.
 */

export const GA_MEASUREMENT_ID = 'G-P40XD66R5X';

/**
 * gtag.js honours window['ga-disable-<ID>'] at runtime, not just at load time.
 * Setting it kills every subsequent hit, including Enhanced-measurement page
 * views we do not control from here. This is our safety net.
 */
const GA_DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}`;

/**
 * Only measure on the real production hosts. Keeps localhost and any preview
 * build out of the property, so Bekkah's numbers are actual visitors.
 */
const MEASURED_HOSTS = ['cedarvault.app', 'cedarvaultapp.com'];

/**
 * Public routes, and the label reported for each. The label is what GA sees —
 * never the raw path — so invitation tokens and vault ids cannot leak.
 */
const PUBLIC_ROUTES: ReadonlyArray<{ test: RegExp; label: string }> = [
  { test: /^\/login\/?$/i, label: '/login' },
  { test: /^\/register\/?$/i, label: '/register' },
  { test: /^\/confirm-email\/?$/i, label: '/confirm-email' },
  { test: /^\/forgot-password\/?$/i, label: '/forgot-password' },
  { test: /^\/reset-password\/?$/i, label: '/reset-password' },
  { test: /^\/2fa-login\/?$/i, label: '/2fa-login' },
  { test: /^\/help\/?$/i, label: '/help' },
  // Token-bearing routes: report the shape, never the token itself.
  { test: /^\/accept-invite\/[^/]+\/?$/i, label: '/accept-invite/:token' },
  { test: /^\/accept-free-vault\/[^/]+\/?$/i, label: '/accept-free-vault/:token' },
];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: string]: unknown;
  }
}

let scriptInjected = false;

function hostIsMeasured(): boolean {
  if (typeof window === 'undefined') return false;
  return MEASURED_HOSTS.includes(window.location.hostname.toLowerCase());
}

/** Returns the GA page label for a path, or null if the path is not public. */
export function publicRouteLabel(pathname: string): string | null {
  const match = PUBLIC_ROUTES.find((r) => r.test.test(pathname));
  return match ? match.label : null;
}

function injectGtag(): void {
  if (scriptInjected) return;
  scriptInjected = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // Must push the raw `arguments` object — that is what gtag.js reads back off
  // dataLayer. An arrow function with rest args pushes a plain array instead,
  // which gtag silently ignores: no error, no data. Hence the cast rather than
  // a normal typed signature.
  const gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  } as (...args: unknown[]) => void;
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    // We send page views by hand, with a scrubbed label. Never automatically.
    send_page_view: false,
    // One session across the marketing site and the app, so a sign-up is
    // credited to the page the visitor actually arrived on.
    linker: { domains: ['cedarvaultapp.com', 'cedarvault.app'] },
  });
}

/**
 * Apply our measurement policy for the current route.
 *
 * - public route  → load GA (once) and send a scrubbed page_view
 * - anything else → hard-disable GA so nothing further is sent
 */
export function trackRoute(pathname: string): void {
  if (!hostIsMeasured()) return;

  const label = publicRouteLabel(pathname);

  if (!label) {
    window[GA_DISABLE_FLAG] = true;
    return;
  }

  window[GA_DISABLE_FLAG] = false;
  injectGtag();

  window.gtag?.('event', 'page_view', {
    page_path: label,
    page_location: `https://${window.location.hostname}${label}`,
    page_title: document.title,
  });
}

export type SignUpMethod = 'self_serve' | 'vault_invite' | 'forever_free';

/**
 * Conversion event. `method` matters for marketing: only `self_serve` is a
 * stranger who found us and chose to sign up. Invited members and Forever Free
 * accounts are not acquisition and should not train an ad audience.
 */
export function trackSignUp(method: SignUpMethod): void {
  if (!hostIsMeasured()) return;
  injectGtag();
  window.gtag?.('event', 'sign_up', { method });
}
